import type { Survey, SurveyScoresByQuestion } from '@engagement-web-app/common';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, type ErrorComponentProps, stripSearchParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { z } from 'zod';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Skeleton } from '@/components/Skeleton';
import { CreatePdfButton } from '@/components/surveys/common/CreatePdfButton';
import { Ranking, RankingSkeleton } from '@/components/surveys/common/Ranking';
import {
    SurveyQuestionsComparisonTable,
    SurveyQuestionsComparisonTableSkeleton,
} from '@/components/surveys/questions/SurveyQuestionsComparisonTable';
import { fetchScoresByQuestion, fetchSurveys } from '@/queries/surveys/system';
import { hiddenInPrint, pageBreakBefore } from '@/styles/print.css';
import {
    useHighLowAnalysisScores,
    useScoresForQuestionsComparisonTable,
    useSurveyAttributes,
    useSurveyQuestionCorrelations,
} from '../-hooks';
import { DefaultRouteQueryParams, RouteQueryParams } from '../-search';
import { HighLowAnalysisChart, HighLowAnalysisChartSkeleton } from './-fragments/HighLowAnalysisChart';
import { QuestionCorrelationsDialog } from './-fragments/QuestionCorrelationsDialog';

const ERROR_MESSAGE = '設問分析の取得に失敗しました。'; // also used below for the in-page query error

const QuestionsRouteQueryParams = RouteQueryParams.extend({
    /** A question to bring into view in the question scores table. */
    questionId: z.string().optional(),
});

export const Route = createFileRoute('/system/_auth/surveys/questions/')({
    loaderDeps({ search: { organizationId, surveyId } }) {
        return { organizationId, surveyId };
    },
    loader: async ({ deps }) => {
        return fetchSurveys(deps.organizationId);
    },
    validateSearch: zodValidator(QuestionsRouteQueryParams),
    search: {
        middlewares: [stripSearchParams(DefaultRouteQueryParams)],
    },
    component: SurveyQuestionsRoute,
    pendingComponent: ContentSkeleton,
    errorComponent: SurveyQuestionsTabError,
});

function SurveyQuestionsTabError({ reset }: ErrorComponentProps) {
    return <ErrorOverlay active onRetry={reset} message={ERROR_MESSAGE} />;
}

export function SurveyQuestionsRoute() {
    const { organizationId, surveyId: selectedSurveyId, questionId } = Route.useSearch();
    const { surveys } = Route.useLoaderData();

    const currentSurveyIdx = surveys.findIndex((s) => s.surveyId === selectedSurveyId);
    const surveyIdsToCompare = surveys.slice(currentSurveyIdx).map((s) => s.surveyId);

    const scoresByQuestionQuery = useQuery({
        queryKey: ['scores-by-question', organizationId, selectedSurveyId],
        queryFn: () => fetchScoresByQuestion(organizationId, { surveyIds: surveyIdsToCompare }),
        enabled: surveyIdsToCompare.length > 0,
    });

    const selectedSurvey = surveys.find((s) => s.surveyId === selectedSurveyId);

    return (
        <>
            {scoresByQuestionQuery.isPending && <ContentSkeleton />}

            <ErrorOverlay
                active={scoresByQuestionQuery.isError}
                onRetry={scoresByQuestionQuery.refetch}
                message={ERROR_MESSAGE}
            />

            {scoresByQuestionQuery.data && selectedSurvey && (
                <Content
                    organizationId={organizationId}
                    scores={scoresByQuestionQuery.data.scores}
                    selectedSurvey={selectedSurvey}
                    focusedQuestionId={questionId}
                    isError={scoresByQuestionQuery.isError}
                    onRetry={scoresByQuestionQuery.refetch}
                />
            )}
        </>
    );
}

type ContentProps = {
    organizationId: string;
    scores: SurveyScoresByQuestion[];
    selectedSurvey: Survey;
    focusedQuestionId?: string;
    isError: boolean;
    onRetry: () => void;
};

function Content({ organizationId, scores, selectedSurvey, focusedQuestionId, isError, onRetry }: ContentProps) {
    // Paginated content has to be expanded before the browser captures the page.
    const [isPrinting, setIsPrinting] = useState(false);
    const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
    const [correlationsQuestionId, setCorrelationsQuestionId] = useState<string | null>(null);
    const [highLowSettingId, setHighLowSettingId] = useState<string | null>(null);

    const selectedSurveyId = selectedSurvey.surveyId;
    const previousSurveyId = selectedSurvey.previousSurveyId;

    const currentSurveyScores = scores.find((s) => s.surveyId === selectedSurveyId)!;

    const categoryMap = new Map<string, string>(
        selectedSurvey.categories.map((category) => [category.categoryId, category.name])
    );

    const surveyAttributesQuery = useSurveyAttributes(organizationId, selectedSurveyId);

    const {
        scoresForQuestionsComparisonTable,
        previousScoresForQuestionsComparisonTable,
        isError: isQuestionsComparisonTableError,
        retry: retryQuestionsComparisonTable,
    } = useScoresForQuestionsComparisonTable(
        organizationId,
        selectedSurveyId,
        previousSurveyId ?? null,
        selectedOptionIds
    );

    const correlationsQuery = useSurveyQuestionCorrelations(
        organizationId,
        selectedSurveyId,
        correlationsQuestionId,
        selectedOptionIds
    );

    const highLowAnalysisQuery = useHighLowAnalysisScores(organizationId, selectedSurveyId, highLowSettingId);

    // The table only hands back an id, so pair it with the question text here.
    const correlationsQuestion = useMemo(() => {
        if (correlationsQuestionId === null) return null;
        const question = currentSurveyScores.questions.find((q) => q.id === correlationsQuestionId);
        if (!question) return null;
        return {
            id: correlationsQuestionId,
            text: question.text,
            categoryName: categoryMap.get(question.categoryId) ?? '',
        };
    }, [correlationsQuestionId, currentSurveyScores, categoryMap]);

    const mappedItemsForRanking = currentSurveyScores.questions.map((r) => ({
        name: r.text,
        di: r.di,
        deviation: r.deviation,
    }));

    return (
        <Flex direction="column" gap="5">
            <Flex justify="end" className={hiddenInPrint}>
                <CreatePdfButton
                    beforeOpenPrint={() => flushSync(() => setIsPrinting(true))}
                    afterClosePrint={() => setIsPrinting(false)}
                />
            </Flex>

            <Flex gap="4">
                <Ranking
                    title="よかった​設問"
                    variant="positive"
                    items={mappedItemsForRanking}
                    isError={isError}
                    onRetry={onRetry}
                />
                <Ranking
                    title="改善必要設問"
                    variant="negative"
                    items={mappedItemsForRanking}
                    isError={isError}
                    onRetry={onRetry}
                />
            </Flex>

            {/* Print layout: rankings, the question scores, then the analysis. */}
            <div className={pageBreakBefore}>
                <SurveyQuestionsComparisonTable
                    scores={scoresForQuestionsComparisonTable.data ?? null}
                    previousScores={previousScoresForQuestionsComparisonTable.data ?? null}
                    attributeSettings={surveyAttributesQuery.data?.attributes ?? null}
                    selectedAttributeOptionIds={selectedOptionIds}
                    onSelectedAttributeOptionIdsChange={setSelectedOptionIds}
                    onShowCorrelations={setCorrelationsQuestionId}
                    isFetching={
                        scoresForQuestionsComparisonTable.isFetching ||
                        previousScoresForQuestionsComparisonTable.isFetching
                    }
                    isError={isQuestionsComparisonTableError}
                    onRetry={retryQuestionsComparisonTable}
                    withPagination={!isPrinting}
                    focusedQuestionId={focusedQuestionId}
                />
            </div>

            <QuestionCorrelationsDialog
                question={correlationsQuestion}
                categories={selectedSurvey.categories}
                correlations={correlationsQuery.data?.correlations}
                isFetching={correlationsQuery.isFetching}
                isError={correlationsQuery.isError}
                onRetry={correlationsQuery.retry}
                onClose={() => setCorrelationsQuestionId(null)}
            />

            {/* Print layout: rankings and the question scores, then the analysis. */}
            <div className={pageBreakBefore}>
                <HighLowAnalysisChart
                    attributeSettings={surveyAttributesQuery.data?.attributes ?? null}
                    settingId={highLowSettingId}
                    onSettingChange={setHighLowSettingId}
                    scores={highLowAnalysisQuery.data?.scores ?? null}
                    isFetching={highLowAnalysisQuery.isFetching}
                    isError={highLowAnalysisQuery.isError}
                    onRetry={highLowAnalysisQuery.retry}
                />
            </div>
        </Flex>
    );
}

function ContentSkeleton() {
    return (
        <Flex direction="column" gap="5">
            <Flex justify="end">
                <Skeleton.Box width={168} height={36} />
            </Flex>

            <Flex gap="4">
                <RankingSkeleton />
                <RankingSkeleton />
            </Flex>

            <SurveyQuestionsComparisonTableSkeleton />

            <HighLowAnalysisChartSkeleton />
        </Flex>
    );
}
